import BoonButterflyIcon from '@assets/svg/boon-butterfly.svg';
import FacebookLoginButton from '@common/auth/facebook-login';
import SignupForm from '@common/auth/signup-form';
import Modal from '@common/modal/modal';
import { signup as doSignup } from '@redux/auth/auth-action';
import AuthState from '@redux/auth/auth-state.interface';
import { AuthActions } from '@redux/auth/auth-types';
import { hideModal, showModal } from '@redux/modal/modal-dispatcher';
import ModalState, { ModalActionSource } from '@redux/modal/modal-state.interface';
import { useQuery } from '@root/src/hooks/use-query';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import styles from './modal.styles';

const SignupModal: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<ThunkDispatch<AuthState, undefined, AuthActions>>();
    const show = useSelector((state: { modal: ModalState }) => state.modal.show);
    const option = useSelector((state: { modal: ModalState }) => state.modal.option);
    const query = useQuery();

    const [showSignupError, setSignupErrorShow] = useState(false);
    const [showGenericError, setGenericErrorShow] = useState(false);

    useEffect(() => {
        setSignupErrorShow(false);
        setGenericErrorShow(false);
    }, [show]);

    const onClickSubmit = () => {
        setGenericErrorShow(false);
        setSignupErrorShow(false);
    };

    const onSignupSubmit = async (email, password, firstName, lastName) => {
        try {
            setGenericErrorShow(false);
            setSignupErrorShow(false);

            let re = query.re;
            // check if there's a redirectUrl from modal options
            if (option && option.redirectUrl) {
                re = option.redirectUrl;
            }

            await dispatch(doSignup({ email, password, firstName, lastName, re }));

            hideModal('signup');
        } catch (error) {
            if (error.response && typeof error.response.status !== 'undefined' && error.response.status === 409) {
                setSignupErrorShow(true);
            } else {
                Sentry.captureException(error);
                setGenericErrorShow(true);
            }

            showModal('signup');
        }
    };

    const onClickLogin = event => {
        event.preventDefault();

        hideModal('signup');

        if (option && option.modalActionSource) {
            showModal('login', { modalActionSource: option.modalActionSource });
        } else {
            showModal('login');
        }
    };

    return (
        <Modal id="signup" contentWrapperClass="relative text-left" titleBarClass="mt-4 mr-4">
            <div className="-mt-4 px-4">
                <style jsx>{styles}</style>

                <img width="35" className="mr-auto ml-auto" src={BoonButterflyIcon} />

                <SignupForm
                    actionSource={option ? option.modalActionSource : ModalActionSource.Default}
                    onSubmit={onSignupSubmit}
                    onClickSubmit={onClickSubmit}
                />

                <div className={'ErrorSignup mb-2 text-center text-error-red text-sm leading-6'}>
                    {showSignupError && <span className="ErrorSignup">{t('validation:existing-email')}</span>}
                    {showGenericError && <span className="ErrorSignupGeneric">{t('validation:error-generic')}</span>}
                </div>

                <div className="px-5 pt-2 mb-4 text-clay text-center tracking-wider">
                    {t('base:already-have-account-label')}{' '}
                    <a
                        href="#"
                        className="AlreadyHaveAccount underline hover:no-underline text-clay"
                        onClick={onClickLogin}
                    >
                        {t('base:already-have-account-link')}
                    </a>
                </div>
            </div>

            <div className="bg-super-boon px-8 py-4">
                <div className="flex flex-row-reverse flex-no-wrap">
                    <div className="flex-grow-0 self-center">
                        <div className="pl-2">
                            <FacebookLoginButton />
                        </div>
                    </div>
                    <div className="flex-grow self-center text-white font-gr-light tracking-wider font-hairline">
                        {t('base:social-media-signup-text')}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default SignupModal;
